import { cn } from "@/lib/utils";
import { forwardRef, HTMLAttributes, ReactNode } from "react";
import { Drawer as DrawerPrimitive } from "vaul";

// Define prop types for components
interface DrawerCloseProps extends HTMLAttributes<HTMLButtonElement> {
  className?: string;
  children?: ReactNode;
}

interface DrawerOverlayProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

interface DrawerContentProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  children?: ReactNode;
}

interface DrawerTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  className?: string;
}

interface DrawerDescriptionProps extends HTMLAttributes<HTMLParagraphElement> {
  className?: string;
}

// Define prop types for non-forwardRef components
interface DrawerHeaderProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

interface DrawerFooterProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const Drawer = ({ shouldScaleBackground = true, ...props }) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    {...props}
  />
);
Drawer.displayName = "Drawer";

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = forwardRef<HTMLButtonElement, DrawerCloseProps>(
  ({ className, children, ...props }, ref) => (
    <DrawerPrimitive.Close className={className} ref={ref} {...props}>
      {children}
    </DrawerPrimitive.Close>
  ),
);

DrawerClose.displayName = DrawerPrimitive.Close.displayName;

const DrawerOverlay = forwardRef<HTMLDivElement, DrawerOverlayProps>(
  ({ className, ...props }, ref) => (
    <DrawerPrimitive.Overlay
      ref={ref}
      className={cn(
        "tw-fixed tw-inset-0 tw-bg-overlay-dark tw-z-[1031]",
        className,
      )}
      {...props}
    />
  ),
);
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = forwardRef<HTMLDivElement, DrawerContentProps>(
  ({ className, children, ...props }, ref) => (
    <DrawerPortal>
      <DrawerOverlay />
      <DrawerPrimitive.Content
        ref={ref}
        className={cn(
          "tw-fixed tw-bottom-0 tw-inset-x-0 tw-mt-24 tw-flex tw-h-auto tw-flex-col tw-rounded-t-12 tw-rounded-b-0 tw-bg-base-white tw-z-[1032]",
          className,
        )}
        {...props}
      >
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  ),
);
DrawerContent.displayName = "DrawerContent";

const DrawerHeader = ({ className, ...props }: DrawerHeaderProps) => (
  <div
    className={cn(
      "tw-grid tw-gap-1.5 tw-text-center sm:tw-text-left tw-border-b tw-border-gray-400 tw-p-16",
      className,
    )}
    {...props}
  />
);
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({ className, ...props }: DrawerFooterProps) => (
  <div
    className={cn(
      "tw-body-1-long tw-mt-auto tw-pb-16 tw-px-16 tw-flex tw-flex-col tw-gap-2 tw-p-16",
      className,
    )}
    {...props}
  />
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = forwardRef<HTMLHeadingElement, DrawerTitleProps>(
  ({ className, ...props }, ref) => (
    <DrawerPrimitive.Title
      ref={ref}
      className={cn(
        "tw-text-lg tw-font-semibold tw-leading-none tw-tracking-tight",
        className,
      )}
      {...props}
    />
  ),
);
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = forwardRef<
  HTMLParagraphElement,
  DrawerDescriptionProps
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn("tw-text-muted-foreground tw-px-16 tw-pt-16", className)}
    {...props}
  />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
};
